// Creative preview pipeline (issue 14). One place for the decisions that make
// creative browsing visually reliable without loading provider media or original
// publishing assets on the render path. These functions are pure so the pipeline
// is tested at a single seam — storage reuse, expiry, source preference, neutral
// fallback, and playable-media extraction — with no live provider call.

// Bumped whenever the derived-preview extraction changes shape. A cached preview
// carries the version that produced it, so a newer version supersedes an older
// derivative rather than being merged with it (see previewDedupeKey).
export const PREVIEW_SOURCE_VERSION = 2;

// A durable, application-owned preview copy lives in the app's own storage and is
// served from a stable same-origin path (`publicFileUrl` in src/server/storage). A
// provider CDN URL is a short-lived source; only stored copies are durable.
export const STORED_PREVIEW_PATH_PREFIX = "/api/files/public/";

export function isDurablePreviewUrl(url: string): boolean {
  return url.startsWith(STORED_PREVIEW_PATH_PREFIX) && !url.includes("..");
}

// A value is displayable as an <img>/<video> source only when it is a real
// http(s) URL or a stored copy's same-origin path. Everything else resolves to
// the neutral fallback rather than a broken media element.
export function isRenderablePreviewUrl(url: string | null | undefined): url is string {
  if (typeof url !== "string" || url.length === 0) return false;
  if (isDurablePreviewUrl(url)) return true;
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

// Dedup key for a derived preview: provider entity identity (provider + provider
// account id + provider entity id) plus the source version that produced it. Two
// requests for the same entity and version reuse one derivative; a renamed entity
// keeps its id-based key, and a new source version yields a new key.
export function previewDedupeKey(
  provider: string,
  accountId: string,
  entityId: string,
  sourceVersion: number = PREVIEW_SOURCE_VERSION,
): string {
  return `${provider}:${accountId}:${entityId}:v${sourceVersion}`;
}

// A derived preview may be reused only while it is unexpired. Both the provider
// source expiry and the cache row's expiry flow through the same check, so a
// stale derivative is re-derived instead of served.
export function isPreviewFresh(
  expiresAt: string | number | Date | null | undefined,
  now: number = Date.now(),
): boolean {
  if (expiresAt == null) return false;
  const ms =
    expiresAt instanceof Date
      ? expiresAt.getTime()
      : typeof expiresAt === "number"
        ? expiresAt
        : Date.parse(expiresAt);
  return Number.isFinite(ms) && ms > now;
}

// Media reference keys, most-compact first. A dedicated thumbnail or poster is a
// recognition-quality derivative; a bare image_url / web_uri can be the full
// publishing asset, so it is only used when no compact reference exists.
const COMPACT_SOURCE_KEYS = [
  "thumbnail_url",
  "video_cover_url",
  "cover_url",
  "poster_url",
] as const;
const FALLBACK_SOURCE_KEYS = ["image_url", "web_uri"] as const;

// A playable media source (an actual video file URL) that creative detail and
// a hover peek may load on demand. These are never used for a table poster,
// so a table row never downloads a video until it is hovered.
// Keys that name a video by themselves: TikTok's `preview_url` is a signed CDN
// URL without a file extension, so these are trusted as given.
const VIDEO_SOURCE_KEYS = ["play_url", "video_url", "playable_url", "preview_url"] as const;
// Generic keys must resolve to a real video file (or HLS manifest). This keeps
// a field ("source", "src") that happens to hold a tracking or landing URL
// from becoming a broken <video>.
const GENERIC_SOURCE_KEYS = ["source", "src"] as const;
const VIDEO_FILE_PATTERN = /\.(mp4|mov|webm|m3u8)(\?|#|$)/i;

function httpString(value: unknown): string | null {
  return typeof value === "string" && /^https?:\/\//i.test(value) ? value : null;
}

// Prefer a compact recognition source over an original publishing asset. Returns
// the chosen URL and whether it is a compact derivative (vs a full asset used
// only because nothing smaller was offered). Searches nested objects/arrays to a
// bounded depth so provider payload shapes do not need to be enumerated.
export function selectRecognitionSource(
  value: unknown,
  depth = 0,
): { url: string; compact: boolean } | null {
  if (!value || typeof value !== "object" || depth > 3) return null;
  const record = value as Record<string, unknown>;
  for (const key of COMPACT_SOURCE_KEYS) {
    const url = httpString(record[key]);
    if (url) return { url, compact: true };
  }
  // A full asset at this level is only a fallback: keep looking for a compact
  // source anywhere in the subtree before settling for it.
  let fallback: { url: string; compact: boolean } | null = null;
  for (const key of FALLBACK_SOURCE_KEYS) {
    const url = httpString(record[key]);
    if (url) {
      fallback = { url, compact: false };
      break;
    }
  }
  for (const child of Object.values(record)) {
    const items = Array.isArray(child) ? child : [child];
    for (const item of items) {
      const nested = selectRecognitionSource(item, depth + 1);
      if (nested?.compact) return nested;
      if (nested && !fallback) fallback = nested;
    }
  }
  return fallback;
}

// Extract a playable media URL from a provider payload, if one is directly
// present. Creative detail uses this to offer playback; the table never does.
// Returns null for the common case where reporting supplies only a poster, so
// detail shows the poster with a video affordance rather than a broken player.
export function playableMediaFromRaw(value: unknown, depth = 0): string | null {
  if (!value || typeof value !== "object" || depth > 3) return null;
  const record = value as Record<string, unknown>;
  for (const key of VIDEO_SOURCE_KEYS) {
    const url = httpString(record[key]);
    if (url) return url;
  }
  for (const key of GENERIC_SOURCE_KEYS) {
    const url = httpString(record[key]);
    if (url && VIDEO_FILE_PATTERN.test(url)) return url;
  }
  for (const child of Object.values(record)) {
    const items = Array.isArray(child) ? child : [child];
    for (const item of items) {
      const nested = playableMediaFromRaw(item, depth + 1);
      if (nested) return nested;
    }
  }
  return null;
}

// Split eligible ad references into bounded chunks so preview enrichment can
// cover every eligible row — not just the first 150 — without an unbounded IN
// list or an oversized provider request. `max` caps total work for a pathological
// workspace; `size` bounds each request.
export function chunkPreviewAds<T>(ads: readonly T[], size = 150, max = 1000): T[][] {
  const bounded = ads.slice(0, Math.max(0, max));
  const batches: T[][] = [];
  for (let index = 0; index < bounded.length; index += size) {
    batches.push(bounded.slice(index, index + size));
  }
  return batches;
}
